import { ParsedUrlQuery } from "querystring"

import { blank } from "./presence"

export function encodeUriJson<T>(obj: T): string {
  return encodeURIComponent(JSON.stringify(obj))
}

export function decodeUriJson<T>(value: string): T {
  return JSON.parse(decodeURIComponent(value))
}

export function getUrlHost(str: string | null | undefined) {
  try {
    if (str) return new URL(str).host
  } catch (e) {
    // Do nothing as the URL is just bad.
    // We don't want a bad URL to hold up rendering of the whole page.
  }
}

export function getUrlParams(str: string | null | undefined) {
  try {
    if (str) return new URL(str).searchParams
  } catch (e) {
    // Do nothing as the URL is just bad.
    // We don't want a bad URL to hold up rendering of the whole page.
  }
}

export function hasParams(url: string, key: string) {
  const urlObj = new URL(url)
  const params = urlObj.searchParams

  // Check if any query parameter starts with "utm_"
  for (const param of params.keys()) {
    if (param.startsWith(key)) {
      return true
    }
  }
  return false
}

export function addParamsToUrl(url: string, params: { [key: string]: string }) {
  try {
    const urlObj = new URL(url)
    const searchParams = urlObj.searchParams
    // Add each key-value pair in the params object to the URL
    for (const [key, value] of Object.entries(params)) {
      searchParams.set(key, value)
    }
    return urlObj.toString()
  } catch (error) {
    // If parsing fails, assume the input is a relative URL
    const [pathPart, queryPart] = url.split("?")
    const searchParams = new URLSearchParams(queryPart || "")
    // Add each key-value pair in the params object to the URL
    for (const [key, value] of Object.entries(params)) {
      searchParams.set(key, value)
    }
    return searchParams.toString()
      ? `${pathPart}?${searchParams.toString()}`
      : pathPart
  }
}

export function addParamToUrl(url: string, param: string, value: string) {
  return setParamToUrl(url, param, value)
}

export function setParamToUrl(url: string, param: string, value: string) {
  try {
    const urlObj = new URL(url)
    const params = urlObj.searchParams
    params.set(param, value)
    return urlObj.toString()
  } catch (error) {
    // If parsing fails, assume the input is a relative URL
    const [pathPart, queryPart] = url.split("?")
    const searchParams = new URLSearchParams(queryPart || "")
    searchParams.set(param, value)
    return searchParams.toString()
      ? `${pathPart}?${searchParams.toString()}`
      : pathPart
  }
}

export function removeParamFromUrl(url: string, param: string) {
  try {
    const urlObj = new URL(url)
    const params = urlObj.searchParams
    params.delete(param)
    return urlObj.toString()
  } catch (error) {
    // If parsing fails, assume the input is a relative URL
    const [pathPart, queryPart] = url.split("?")
    const searchParams = new URLSearchParams(queryPart || "")
    searchParams.delete(param)
    return searchParams.toString()
      ? `${pathPart}?${searchParams.toString()}`
      : pathPart
  }
}

export function rewriteUrlWithHostAndProtocol(
  sourceUrl: string,
  targetUrl: string
) {
  if (blank(targetUrl)) {
    return sourceUrl
  }

  // Create URL objects from both URLs
  const source = new URL(sourceUrl)
  const target = new URL(targetUrl)

  // Replace the target's host and protocol with those from the source
  target.hostname = source.hostname
  target.protocol = source.protocol
  if (source.port !== "80" && source.port !== "443") {
    target.port = source.port
  }

  // Return the updated target URL as a string
  return target.toString()
}

export function copyUtmParamsInto(
  query: ParsedUrlQuery,
  retSearchParams: URLSearchParams
) {
  const keys = Object.keys(query)
  if (keys.length > 0) {
    keys.forEach((key) => {
      if (key.startsWith("utm_")) {
        const value = query[key]
        if (value) {
          retSearchParams.append(
            key,
            typeof value === "string" ? value : value[0]
          )
        }
      }
    })
  }
  return retSearchParams
}
